/**
 * 校准管理器：把"发一条校准命令"变成"一个有进度、有结论、能取消、超时会有交代"的流程。
 *
 * ## 一、为什么进度不能自己造
 *
 * 校准命令的 `COMMAND_ACK` 只能回答"飞控收下了"，回答不了"校到哪一步了"。磁力计校准的
 * 真实进度只在 `MAG_CAL_PROGRESS` 里（`completion_pct` / `attempt` / `completion_mask`），
 * 结论只在 `MAG_CAL_REPORT` 里（`cal_status` / `fitness` / `autosaved` / 偏移量）。
 * 所以这里**不从时间推算进度**（"大概过了 30 秒，画到一半吧"）：那条进度条会在飞控还在
 * 转车身时提前满格、在用户停下来时继续前进，而两个现象都只能靠真机复现。
 * 收不到进度就如实让 `progressPct` 为 `null`（界面显示"已开始，等待飞控返回进度"）。
 *
 * ## 二、为什么全程只认枚举名
 *
 * `cal_status` 有 11 个取值，其中 6 个是**具体**的失败原因（方向不对 / 半径不对 / 残差过大…），
 * 下一步该做什么完全相反。数字一律从 `@hgc/mavlink` 的生成表反查（`enumByName`），
 * 语义一律走 `mag-status.ts` 已有的映射（那里逐条写清了原因与建议）。
 * 这个文件里没有任何一个手写的 `MAG_CAL_STATUS` 编号。
 *
 * ## 三、命令一律交给 `CommandManager`
 *
 * 校准是"发一条命令、等很久、中间还可能被拒绝"的典型。ACK 路由、重传计数（`confirmation`）、
 * `IN_PROGRESS` 长任务、以及"失败 ACK + 紧随其后的 STATUSTEXT 原因"关联，都在
 * `commands/manager.ts` 里做过了。这里再实现一遍不会更快，只会让同一个命令编号出现两条
 * 并行的等待链 —— 表现是"校准成功/失败随机"。
 *
 * 一个副作用需要说明：校准类命令在上游是 `critical` 等级（`PREFLIGHT_CALIBRATION`、
 * `DO_START_MAG_CAL`、`ACCELCAL_VEHICLE_POS`），`CommandManager` 会要求二次确认。
 * 管理器不弹窗（它不认识界面），而向导界面在"开始"按钮之前已经把准备事项与风险显示给
 * 用户了 —— 用户点那一下就是确认，所以这里统一下发 `confirmed: true`。
 *
 * ## 四、`start()` 什么时候兑现
 *
 * 不是"命令被接受"就兑现，而是**等本次校准有结论**（或超时/断链/取消/销毁）。
 * 磁力计校准在真机上要几十秒，界面要的是"最终怎么样"，而不是"命令发出去了"。
 * 所以长任务会一直等到 {@link CalibrationManager.conclude} 收尾为止。
 */

import { commandId } from '@hgc/ardupilot';
import {
  decodePayload,
  enumByName,
  enumValue,
  messageById,
  messageIdOf,
  type DecodedValues,
  type MavlinkFrame,
} from '@hgc/mavlink';

import {
  DEFAULT_COMMAND_TIMERS,
  type CommandManager,
  type CommandResult,
  type CommandTimers,
  type CommandVehicle,
} from '../commands';
import { createEventBus, type EventBus, type Unsubscribe } from '../events';
import type { LinkStateEvent, StatustextEntry } from '../vehicle';

import {
  MAG_CAL_PARAMS,
  MAG_YAW_PARAMS,
  calibrationKindInfo,
  paramIndexByLabel,
  resolveParamValue,
  type CalibrationKind,
  type CalibrationKindInfo,
} from './kinds';
import { assessFitness, magCalStatusInfo, type MagCalStatusInfo } from './mag-status';
import type {
  AccelPositionPrompt,
  CalibrationActionOutcome,
  CalibrationEvents,
  CalibrationFailure,
  CalibrationFailureCode,
  CalibrationManagerOptions,
  CalibrationOutcome,
  CalibrationPhase,
  CalibrationReport,
  CalibrationStartRequest,
  CalibrationState,
  CompassProgress,
  CompassResult,
  ParamReadback,
} from './types';

/* ────────────────────────────── 协议查表 ────────────────────────────── */

/** 进度帧与结论帧的消息编号。查表得到，不写数字（`191`/`192` 记错一位会静默收错帧）。 */
const MAG_PROGRESS_ID = messageIdOf('MAG_CAL_PROGRESS') ?? null;
const MAG_REPORT_ID = messageIdOf('MAG_CAL_REPORT') ?? null;
/** 加速度计校准的"请摆成某个方向"是飞控用一条 `COMMAND_LONG` 指挥地面站做的。 */
const COMMAND_LONG_ID = messageIdOf('COMMAND_LONG') ?? null;
const ACCELCAL_COMMAND_ID = commandId('MAV_CMD_ACCELCAL_VEHICLE_POS') ?? null;

/**
 * `MAG_CAL_STATUS` 在生成表里的名字。
 *
 * 上游把它定义在 `common.xml`，条目名就是 `MAG_CAL_STATUS`（生成表因此也是这个名字）；
 * 带 `MAV_` 前缀的写法在其它 MAVLink 实现里也常见，所以两个都试一遍。
 * 查到名字只用于显示，不影响结论（语义来自 `mag-status.ts` 的表）。
 */
const MAG_CAL_STATUS_ENUM_NAMES: readonly string[] = ['MAV_MAG_CAL_STATUS', 'MAG_CAL_STATUS'];

/** 加速度计六面校准的步数（上游 `ACCELCAL_VEHICLE_POS_*` 的 1..6）。 */
const ACCEL_POSITION_TOTAL = 6;
/** `MAV_CMD_ACCELCAL_VEHICLE_POS` 的姿态参数标签（与 `kinds.ts` 的标注同一份）。 */
const ACCEL_POSITION_PARAM_LABEL = '姿态';

const ACCEL_POSITION_SUCCESS = enumValue('ACCELCAL_VEHICLE_POS', 'ACCELCAL_VEHICLE_POS_SUCCESS') ?? null;
const ACCEL_POSITION_FAILED = enumValue('ACCELCAL_VEHICLE_POS', 'ACCELCAL_VEHICLE_POS_FAILED') ?? null;

/** 每个摆放方向的操作说明。键是上游枚举名。 */
const ACCEL_POSITION_TEXT: Readonly<Record<string, string>> = {
  ACCELCAL_VEHICLE_POS_LEVEL: '把机体水平放好（正面朝上、机头朝前）',
  ACCELCAL_VEHICLE_POS_LEFT: '把机体左侧朝下，机头方向不变',
  ACCELCAL_VEHICLE_POS_RIGHT: '把机体右侧朝下，机头方向不变',
  ACCELCAL_VEHICLE_POS_NOSEDOWN: '把机头朝下立起来（尾部朝上）',
  ACCELCAL_VEHICLE_POS_NOSEUP: '把机头朝上立起来（尾部朝下）',
  ACCELCAL_VEHICLE_POS_BACK: '把机体翻过来，底面朝上',
};

/**
 * 链路状态里"这次校准已经不可能再有结果"的那几个。
 *
 * `idle`/`connecting` 也算：校准过程中链路不该回到这两个状态（那意味着用户把链路关了）。
 */
const LINK_LOST_STATES: ReadonlySet<string> = new Set([
  'idle',
  'connecting',
  'silent',
  'offline',
  'error',
]);

/** 保留的最近状态文本条数：失败时用来还原"飞控到底说了什么"。 */
const STATUSTEXT_KEEP = 8;

/* ────────────────────────────── 小工具 ────────────────────────────── */

function numberField(values: DecodedValues, name: string): number | null {
  const value = values[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** 取数组字段（`completion_mask` 是 `uint8_t[10]`）。解出来的可能是视图，一律复制。 */
function numberArrayField(values: DecodedValues, name: string): readonly number[] {
  const value = values[name];
  if (value === undefined || typeof value === 'string') return [];
  if (typeof value === 'number' || typeof value === 'bigint') return [];
  const out: number[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const entry: number | bigint | undefined = value[index];
    out.push(entry === undefined ? 0 : typeof entry === 'number' ? entry : Number(entry));
  }
  return out;
}

/** 数值 → 枚举名。查不到返回 `null`（**不猜**）。 */
function enumEntryName(enumName: string, value: number): string | null {
  const definition = enumByName(enumName);
  if (definition === undefined) return null;
  return definition.entries.find((entry) => entry.value === value)?.name ?? null;
}

function magCalStatusName(value: number): string | null {
  for (const name of MAG_CAL_STATUS_ENUM_NAMES) {
    const found = enumEntryName(name, value);
    if (found !== null) return found;
  }
  return null;
}

/** 位掩码 → 位序号列表（0..7）。0 表示"没有任何罗盘"，不是"全部"。 */
function maskBits(mask: number): readonly number[] {
  const bits: number[] = [];
  if (!Number.isFinite(mask) || mask <= 0) return bits;
  for (let bit = 0; bit < 8; bit += 1) {
    if ((mask & (1 << bit)) !== 0) bits.push(bit);
  }
  return bits;
}

/** 用户选的罗盘 → 掩码。空/未指定时返回 0，在 `DO_START_MAG_CAL` 里是"由飞控决定"。 */
function compassMask(compassIds: readonly number[] | undefined): number {
  let mask = 0;
  for (const id of compassIds ?? []) {
    if (!Number.isInteger(id) || id < 0 || id > 7) continue;
    mask |= 1 << id;
  }
  return mask;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

function phaseForOutcome(outcome: CalibrationOutcome): CalibrationPhase {
  switch (outcome) {
    case 'success':
      return 'success';
    case 'failed':
      return 'failed';
    case 'interrupted':
      return 'interrupted';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unsupported';
  }
}

/** 失败码附带的"下一步该做什么"。没有可行建议时为 `null`。 */
function adviceForCode(code: CalibrationFailureCode): string | null {
  switch (code) {
    case 'link-lost':
      return '先恢复链路（检查串口/网口、机体是否上电），再重新校准 —— 这次的结果无法确认。';
    case 'no-ack':
      return '确认链路质量与波特率；也可以先把遥测流打开（收不到任何帧时命令的确认也回不来），然后重新校准。';
    case 'blocked':
      return '按上面列出的阻塞原因逐条解决后重试。';
    case 'send-error':
      return '检查链路是否仍在打开状态，然后重试。';
    case 'timeout':
    case 'stalled':
      return '读一遍飞控日志确认这次校准的真实结果；确认机体已静止、周围无干扰后重新校准。';
    case 'rejected':
      return '飞控拒绝了这条命令：多数情况是解锁前检查（PreArm）没过，或机体还处于解锁/飞行状态。解决后重试。';
    case 'unsupported-by-vehicle':
      return '这版固件不支持该命令：请升级飞控固件，或改用厂家工具完成这一步。';
    case 'unknown-kind':
      return '确认固件与地面站版本；如果是新增的校准方式，需要先在本仓库的协议表里收录它。';
    case 'not-implemented':
      return '这一步请用厂家工具或命令行完成。';
    case 'param-not-found':
    case 'command-not-found':
      return '本仓库的协议表与固件版本可能不匹配：升级本仓库或固件后再试（这里刻意不下发，以免校错传感器）。';
    case 'cancelled':
      return '可以重新开始校准。';
    case 'disposed':
      return '校准管理器已销毁（通常是界面被关闭或切换了机型）：重新打开校准向导再试。';
    default:
      return null;
  }
}

/* ────────────────────────────── 内部状态 ────────────────────────────── */

interface ReadbackWaiter {
  readonly handle: number;
  readonly settle: (value: number | null) => void;
}

/** 一次校准的运行态。终态后整块丢弃（结论在 `CalibrationReport` 里）。 */
interface ActiveRun {
  readonly info: CalibrationKindInfo;
  readonly request: CalibrationStartRequest;
  /** 磁力计掩码：0 表示"由飞控决定"，取消/接受时要把同一个掩码带回去。 */
  readonly mask: number;
  readonly startedAt: number;
  phase: CalibrationPhase;
  /** 面向飞手的一句话（界面直接显示）。 */
  message: string;
  /** 期望收到结论的罗盘：来自用户选择、进度帧的 `cal_mask` 或结论帧的 `cal_mask`。 */
  readonly expected: Set<number>;
  readonly progress: Map<number, CompassProgress>;
  readonly results: Map<number, CompassResult>;
  readonly statustexts: StatustextEntry[];
  command: CommandResult | null;
  prompt: AccelPositionPrompt | null;
  /** 磁力计专用：飞控是否已自动保存（`null` = 非磁力计）。 */
  autosaved: boolean | null;
  /** 成功但没自动保存 → 界面应显示"接受校准"。 */
  acceptAvailable: boolean;
  readback: readonly ParamReadback[];
  absoluteHandle: number | null;
  stallHandle: number | null;
  concluded: boolean;
  /** 结论产生时兑现（`start()` 返回的就是它）。 */
  readonly settle: (report: CalibrationReport) => void;
}

interface OpenedRun {
  readonly run: ActiveRun;
  readonly done: Promise<CalibrationReport>;
}

interface LaunchPlanOk {
  readonly ok: true;
  readonly command: string;
  readonly values: readonly number[];
  readonly mask: number;
  readonly forceLong: boolean;
}

interface LaunchPlanFailure {
  readonly ok: false;
  readonly failure: CalibrationFailure;
}

type LaunchPlan = LaunchPlanOk | LaunchPlanFailure;

type MaskedPlan =
  | { readonly ok: true; readonly values: readonly number[] }
  | { readonly ok: false; readonly failure: CalibrationFailure };

/* ────────────────────────────── 管理器 ────────────────────────────── */

export class CalibrationManager {
  readonly events: EventBus<CalibrationEvents>;

  private readonly vehicle: CommandVehicle;
  private readonly commands: CommandManager;
  private readonly now: () => number;
  private readonly timers: CommandTimers;
  private readonly timeouts: Partial<Readonly<Record<CalibrationKind, number>>>;
  private readonly readbackTimeoutMs: number;
  private readonly params: CalibrationManagerOptions['params'];
  private readonly paramMetadata: CalibrationManagerOptions['paramMetadata'];
  private readonly unsubscribes: Unsubscribe[] = [];

  private run: ActiveRun | null = null;
  private lastReport: CalibrationReport | null = null;
  private lastReadback: readonly ParamReadback[] = Object.freeze([]);
  private snapshot: CalibrationState;
  /** 上次成功但没自动保存的磁力计校准用的是哪个掩码（`accept()` 要带回去）。 */
  private acceptedMask = 0;
  private acceptAvailable = false;
  /** 本次会话里收到过的参数值（回读用；不区分是谁请求的）。 */
  private readonly receivedParams = new Map<string, number>();
  private readonly readbackWaiters = new Map<string, ReadbackWaiter>();
  private disposed = false;

  constructor(options: CalibrationManagerOptions) {
    this.vehicle = options.vehicle;
    this.commands = options.commands;
    this.now = options.now ?? (() => Date.now());
    this.timers = options.timers ?? DEFAULT_COMMAND_TIMERS;
    this.timeouts = options.timeouts ?? {};
    this.readbackTimeoutMs = options.readbackTimeoutMs ?? 1500;
    this.params = options.params;
    this.paramMetadata = options.paramMetadata;
    this.events = createEventBus<CalibrationEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    this.unsubscribes.push(this.vehicle.events.on('frame', (frame) => this.handleFrame(frame)));
    this.unsubscribes.push(this.vehicle.events.on('statustext', (entry) => this.handleStatustext(entry)));
    this.unsubscribes.push(
      this.vehicle.events.on('paramValue', (event) => this.handleParamValue(event.name, event.value)),
    );
    this.unsubscribes.push(
      this.vehicle.events.on('linkState', (change) => this.handleLinkState(change)),
    );

    this.snapshot = this.buildState();
  }

  /* ────────────────────── 只读视图 ────────────────────── */

  /** 当前状态快照（界面挂载时直接取，不必等下一个事件）。 */
  get state(): CalibrationState {
    return this.snapshot;
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  /** 订阅者数量（泄漏检查）。传 `type` 只看某一路事件。 */
  listenerCount(type?: keyof CalibrationEvents): number {
    return this.events.listenerCount(type);
  }

  /* ────────────────────── 开始 ────────────────────── */

  /**
   * 开始一次校准。返回的 Promise 在**有结论时**兑现（成功、失败、中断、取消、不支持），
   * 永远不会 reject —— "这个种类我们不做"是一种结论，不是异常。
   *
   * 长任务（磁力计、六面加速度计）会一直等到有结论或超时，见文件头第四条。
   */
  start(request: CalibrationStartRequest): Promise<CalibrationReport> {
    const info = calibrationKindInfo(String(request.kind));

    if (this.disposed) {
      return Promise.resolve(
        this.standaloneReport(null, null, 'failed', {
          code: 'disposed',
          text: '校准管理器已销毁，这次校准没有下发',
          advice: adviceForCode('disposed'),
          reasons: Object.freeze([]),
        }),
      );
    }

    if (info === null) {
      // 认不出的种类：如实说"不认识"，一个字节都不发。假装执行会让用户以为校过了。
      return Promise.resolve(
        this.standaloneReport(null, null, 'unsupported', {
          code: 'unknown-kind',
          text: `本仓库不认识校准种类 “${String(request.kind)}”：没有下发任何命令`,
          advice: adviceForCode('unknown-kind'),
          reasons: Object.freeze([]),
        }),
      );
    }

    if (!info.supported) {
      return Promise.resolve(
        this.standaloneReport(info, info.unsupportedReason, 'unsupported', {
          code: 'not-implemented',
          text: info.unsupportedReason ?? `${info.label}不在本向导的支持范围内`,
          advice: adviceForCode('not-implemented'),
          reasons: Object.freeze([]),
        }),
      );
    }

    if (this.run !== null) {
      // 刻意**不**发布这份结论：它描述的是一次被拒绝的请求，不是正在进行的那些罗盘的状态，
      // 覆盖掉会让界面上的进度条凭空消失。
      return Promise.resolve(
        this.makeReport(this.run.info, 'failed', {
          code: 'blocked',
          text: `上一次校准（${this.run.info.label}）还在进行中：请先取消它，或等它给出结论`,
          advice: adviceForCode('blocked'),
          reasons: Object.freeze([]),
        }),
      );
    }

    const planned = this.planLaunch(info, request);
    if (!planned.ok) {
      return Promise.resolve(this.standaloneReport(info, null, 'failed', planned.failure));
    }

    const { run, done } = this.openRun(info, request, planned.mask);
    this.run = run;
    this.acceptAvailable = false;
    this.publish();
    this.armTimeouts(run);

    // 先挂好运行态再下发：飞控可能**先**开始推 `MAG_CAL_PROGRESS`、后回 `COMMAND_ACK`
    // （校准开始得快时就是这样），那几帧不能丢。
    void this.dispatch(run, planned);
    return done;
  }

  /** 下发开始命令，并按结论来源分流后续等待。 */
  private async dispatch(run: ActiveRun, planned: LaunchPlanOk): Promise<void> {
    const info = run.info;
    const result = await this.commands.execute({
      command: planned.command,
      params: planned.values,
      // 见文件头第三条：向导界面上的"开始"就是二次确认
      confirmed: true,
      ...(planned.forceLong ? { forceLong: true } : {}),
    });

    // 等待期间可能已经超时/断链/被取消/被销毁 —— 那些结论优先，这里不能再补一个
    if (run.concluded) return;
    run.command = result;

    if (result.status === 'unsupported') {
      this.conclude(run, 'unsupported', {
        code: 'unsupported-by-vehicle',
        text: `飞控不支持${info.label}（${result.text}）：这次校准没有开始`,
        advice: adviceForCode('unsupported-by-vehicle'),
        reasons: Object.freeze([...run.statustexts]),
      });
      return;
    }

    if (!result.ok) {
      const code = this.failureCodeOf(result);
      this.conclude(run, 'failed', {
        code,
        text: `${info.label}没能开始：${result.text}`,
        advice: adviceForCode(code),
        reasons: Object.freeze([...run.statustexts]),
      });
      return;
    }

    switch (info.conclusion) {
      case 'mag-report':
        run.phase = 'running';
        run.message = `${info.label}已开始：${
          info.requiresRotation ? '按提示缓慢转动整架机体，' : ''
        }等待飞控返回进度…`;
        this.publish();
        return;
      case 'accel-positions':
        run.phase = 'awaiting-position';
        run.message = '已开始：等待飞控指示第一个摆放方向…';
        this.publish();
        return;
      default:
        // 没有过程遥测：命令被接受就是结论（参数回读异步进行，不阻塞结论）
        this.concludeSuccess(run, `${info.label}完成`, result.text);
    }
  }

  /* ────────────────────── 取消 / 接受 / 摆放 ────────────────────── */

  /**
   * 中途取消。磁力计族会发 `DO_CANCEL_MAG_CAL` 让飞控停下手上的活；其余种类飞控没有
   * 对应的"取消"命令，只就地停止跟踪（并在文案里说明这一点）。无论发不发得出去，
   * 本地状态都会清理干净，之后可以重新开始。
   */
  async cancel(): Promise<CalibrationActionOutcome> {
    const run = this.run;
    if (this.disposed || run === null) {
      return { ok: false, sent: false, result: null, text: '当前没有正在进行的校准' };
    }

    let result: CommandResult | null = null;
    let sent = false;
    let note: string;

    if (run.info.usesMagCalMask) {
      const planned = this.planMaskedCommand('MAV_CMD_DO_CANCEL_MAG_CAL', run.mask);
      if (planned.ok) {
        result = await this.commands.execute({
          command: 'MAV_CMD_DO_CANCEL_MAG_CAL',
          params: planned.values,
          confirmed: true,
        });
        sent = true;
        note = result.ok ? '已通知飞控停止校准' : `取消命令没能生效（${result.text}）：已就地停止跟踪`;
      } else {
        note = `没能构造取消命令（${planned.failure.text}）：已就地停止跟踪`;
      }
    } else {
      note = `${run.info.label}在飞控那边没有"取消"这一步：已就地停止跟踪，飞控可能仍在执行`;
    }

    if (run.concluded) {
      // 取消命令还在飞的时候结论已经产生了（例如飞控同时回了成功报告）
      return { ok: sent, sent, result, text: note };
    }

    this.conclude(run, 'cancelled', {
      code: 'cancelled',
      text: `用户取消了${run.info.label}：${note}`,
      advice: adviceForCode('cancelled'),
      reasons: Object.freeze([...run.statustexts]),
    });
    return { ok: true, sent, result, text: note };
  }

  /**
   * 接受磁力计校准（`DO_ACCEPT_MAG_CAL`）。
   *
   * 只有"校准成功但飞控没有自动保存"时才需要它 —— 那是默认路径：我们默认不带自动保存
   * 发起校准，好让用户先看清 fitness 再决定要不要写进参数。
   */
  async accept(): Promise<CalibrationActionOutcome> {
    if (this.disposed) {
      return { ok: false, sent: false, result: null, text: '校准管理器已销毁' };
    }
    const report = this.lastReport;
    if (report === null || report.outcome !== 'success' || report.autosaved !== false) {
      return {
        ok: false,
        sent: false,
        result: null,
        text:
          report === null
            ? '还没有成功完成的磁力计校准'
            : report.autosaved === true
              ? '飞控已经自动保存了这次校准，不需要再接受一次'
              : '只有成功的磁力计校准才能被接受',
      };
    }
    const planned = this.planMaskedCommand('MAV_CMD_DO_ACCEPT_MAG_CAL', this.acceptedMask);
    if (!planned.ok) {
      return { ok: false, sent: false, result: null, text: planned.failure.text };
    }
    const result = await this.commands.execute({
      command: 'MAV_CMD_DO_ACCEPT_MAG_CAL',
      params: planned.values,
      confirmed: true,
    });
    if (result.ok) {
      // 写回去的是**用户这次接受**的结果，与 `autosaved`（飞控自己保存）是两件事，
      // 所以报告里的 `autosaved` 不改（那是事实记录），只把"可以接受"收起来。
      this.acceptAvailable = false;
      this.publish();
    }
    return { ok: result.ok, sent: true, result, text: result.text };
  }

  /** 六面加速度计：告诉飞控"我已经摆好了"。不传 `position` 时用当前提示里的那个。 */
  async placeVehicle(position?: number): Promise<CalibrationActionOutcome> {
    const run = this.run;
    if (this.disposed || run === null) {
      return { ok: false, sent: false, result: null, text: '当前没有正在进行的校准' };
    }
    const target = position ?? run.prompt?.position ?? null;
    if (target === null) {
      return { ok: false, sent: false, result: null, text: '飞控还没有要求摆放任何方向' };
    }
    if (paramIndexByLabel('MAV_CMD_ACCELCAL_VEHICLE_POS', ACCEL_POSITION_PARAM_LABEL) === null) {
      return {
        ok: false,
        sent: false,
        result: null,
        text: '协议表里没有 MAV_CMD_ACCELCAL_VEHICLE_POS 的姿态参数位：为免发错，这里不下发',
      };
    }
    const result = await this.commands.execute({
      command: 'MAV_CMD_ACCELCAL_VEHICLE_POS',
      params: [target],
      confirmed: true,
    });
    return { ok: result.ok, sent: true, result, text: result.text };
  }

  /* ────────────────────── 销毁 ────────────────────── */

  /** 取消全部订阅、清掉所有定时器与等待者，并把进行中的校准收成"结果未知"。 */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.unsubscribes) off();
    this.unsubscribes.length = 0;

    const run = this.run;
    if (run !== null) {
      // 飞控那边可能仍在跑，所以是"结果未知"而不是"失败"（与 `commands` 的 `disposed` 同义）
      this.conclude(run, 'interrupted', {
        code: 'disposed',
        text: '校准管理器已销毁：这次校准的结果未知',
        advice: adviceForCode('disposed'),
        reasons: Object.freeze([...run.statustexts]),
      });
    }
    this.clearReadbackWaiters();
    this.events.clear();
  }

  /* ────────────────────── 内部：请求规划 ────────────────────── */

  /**
   * 把请求变成 `param1…param7`。
   *
   * 参数序号一律反查元数据（`paramIndexByLabel`）：查不到就**拒绝下发** ——
   * "某一项填 0"的下场不是报错，而是升级固件后把另一个传感器校掉。
   */
  private planLaunch(info: CalibrationKindInfo, request: CalibrationStartRequest): LaunchPlan {
    const launch = info.launch;
    if (launch === null || commandId(launch.command) === undefined) {
      return {
        ok: false,
        failure: {
          code: 'command-not-found',
          text: `协议表里找不到${info.label}对应的命令（${
            launch === null ? '未定义' : launch.command
          }）：没有下发任何命令`,
          advice: adviceForCode('command-not-found'),
          reasons: Object.freeze([]),
        },
      };
    }

    const values = [0, 0, 0, 0, 0, 0, 0];
    const mask = compassMask(request.compassIds);
    const put = (label: string, value: number): CalibrationFailure | null => {
      const index = paramIndexByLabel(launch.command, label);
      if (index === null || index < 1 || index > 7) {
        return {
          code: 'param-not-found',
          text: `协议元数据里没有 ${launch.command} 的「${label}」参数位：拒绝下发（参数位填错会把别的传感器校掉）`,
          advice: adviceForCode('param-not-found'),
          reasons: Object.freeze([]),
        };
      }
      values[index - 1] = value;
      return null;
    };

    if (info.kind === 'mag') {
      // `DO_START_MAG_CAL` 的 param1 是掩码：0 = 由飞控决定校准哪些罗盘
      const failed = put(launch.label, mask);
      if (failed !== null) return { ok: false, failure: failed };
      const extras: readonly (readonly [string, number])[] = [
        [MAG_CAL_PARAMS.retry, request.retryOnFailure === true ? 1 : 0],
        // 默认**不**自动保存：先让用户看到 fitness 与各罗盘的结论，再决定要不要写入参数
        [MAG_CAL_PARAMS.autosave, request.autosave === true ? 1 : 0],
        [MAG_CAL_PARAMS.delay, request.delaySec ?? 0],
        [MAG_CAL_PARAMS.autoreboot, request.autoreboot === true ? 1 : 0],
      ];
      for (const [label, value] of extras) {
        const failedExtra = put(label, value);
        if (failedExtra !== null) return { ok: false, failure: failedExtra };
      }
    } else if (info.kind === 'mag-yaw') {
      // 这条命令的启动值是**航向**（度），不是掩码 —— `usesMagCalMask` 说的是
      // "取消/接受时要用掩码语义"，与启动参数无关
      const yaw = request.yawDeg;
      if (yaw === undefined || !Number.isFinite(yaw)) {
        return {
          ok: false,
          failure: {
            code: 'blocked',
            text: '已知航向磁校准需要先给出机头的真实航向（度）：没有下发命令',
            advice: '用指南针或地图量出机头当前指向，填进界面后重试。',
            reasons: Object.freeze([]),
          },
        };
      }
      const failed = put(MAG_YAW_PARAMS.yaw, yaw);
      if (failed !== null) return { ok: false, failure: failed };
      // 经纬度刻意留 0：飞控会用当前位置（`kinds.ts` 的准备事项里也这么写）。
      // 用户没指定罗盘时也不写掩码参数，让飞控按自己的判断来。
      if (mask !== 0) {
        const maskFailure = put(MAG_YAW_PARAMS.mask, mask);
        if (maskFailure !== null) return { ok: false, failure: maskFailure };
      }
    } else {
      const value = resolveParamValue(launch.value);
      if (value === null) {
        return {
          ok: false,
          failure: {
            code: 'command-not-found',
            text: `${launch.command} 的「${launch.label}」取值在枚举表里查不到：为避免校错传感器，这里不下发`,
            advice: adviceForCode('command-not-found'),
            reasons: Object.freeze([]),
          },
        };
      }
      const failed = put(launch.label, value);
      if (failed !== null) return { ok: false, failure: failed };
    }

    return {
      ok: true,
      command: launch.command,
      values,
      mask,
      // `FIXED_MAG_CAL_YAW` 的纬度/经度是**十进制度**，而 `COMMAND_INT` 的同两个字段是
      // "度 ×1e7"。这条命令宁可走 `COMMAND_LONG`：我们不带坐标（留 0 让飞控用当前位置），
      // 一旦被 `COMMAND_INT` 重新解释，0 就变成"赤道/本初子午线"，磁偏角会取错地方。
      forceLong: info.kind === 'mag-yaw',
    };
  }

  /** 只带一个掩码参数的命令（取消/接受磁力计校准）。 */
  private planMaskedCommand(command: string, mask: number): MaskedPlan {
    const index = paramIndexByLabel(command, MAG_CAL_PARAMS.mask);
    if (index === null || index < 1 || index > 7) {
      return {
        ok: false,
        failure: {
          code: 'param-not-found',
          text: `协议元数据里没有 ${command} 的「${MAG_CAL_PARAMS.mask}」参数位`,
          advice: adviceForCode('param-not-found'),
          reasons: Object.freeze([]),
        },
      };
    }
    const values = [0, 0, 0, 0, 0, 0, 0];
    values[index - 1] = mask;
    return { ok: true, values };
  }

  private failureCodeOf(result: CommandResult): CalibrationFailureCode {
    switch (result.status) {
      case 'unsupported':
        return 'unsupported-by-vehicle';
      case 'blocked':
        return 'blocked';
      case 'send-error':
        return 'send-error';
      case 'no-ack':
        return 'no-ack';
      case 'disposed':
        return 'disposed';
      default:
        return 'rejected';
    }
  }

  private openRun(info: CalibrationKindInfo, request: CalibrationStartRequest, mask: number): OpenedRun {
    let settle!: (report: CalibrationReport) => void;
    const done = new Promise<CalibrationReport>((resolve) => {
      settle = resolve;
    });
    return {
      done,
      run: {
        info,
        request,
        mask,
        startedAt: this.now(),
        phase: 'starting',
        message: `${info.label}：已下发开始命令，等待飞控确认…`,
        expected: new Set<number>(request.compassIds ?? []),
        progress: new Map<number, CompassProgress>(),
        results: new Map<number, CompassResult>(),
        statustexts: [],
        command: null,
        prompt: null,
        autosaved: null,
        acceptAvailable: false,
        readback: Object.freeze([]),
        absoluteHandle: null,
        stallHandle: null,
        concluded: false,
        settle,
      },
    };
  }

  private armTimeouts(run: ActiveRun): void {
    const timeoutMs = run.request.timeoutMs ?? this.timeouts[run.info.kind] ?? run.info.timeoutMs;
    run.absoluteHandle = this.timers.set(() => this.onAbsoluteTimeout(run), timeoutMs);
    this.touchStall(run);
  }

  /** 进度看门狗：每收到一帧进度/结论就重新计时。 */
  private touchStall(run: ActiveRun): void {
    if (run.stallHandle !== null) {
      this.timers.clear(run.stallHandle);
      run.stallHandle = null;
    }
    const window = run.info.stallTimeoutMs;
    if (window === null || run.concluded) return;
    run.stallHandle = this.timers.set(() => this.onStalled(run), window);
  }

  private clearRunTimers(run: ActiveRun): void {
    if (run.absoluteHandle !== null) this.timers.clear(run.absoluteHandle);
    if (run.stallHandle !== null) this.timers.clear(run.stallHandle);
    run.absoluteHandle = null;
    run.stallHandle = null;
  }

  private onAbsoluteTimeout(run: ActiveRun): void {
    if (run.concluded || this.run !== run) return;
    const timeoutMs = run.request.timeoutMs ?? this.timeouts[run.info.kind] ?? run.info.timeoutMs;
    const missing = this.missingCompasses(run);
    this.conclude(run, 'failed', {
      code: 'timeout',
      text:
        `超过 ${Math.round(timeoutMs / 1000)} 秒仍未收到${run.info.label}的结论` +
        (missing.length === 0 ? '' : `（罗盘 ${missing.map((id) => `#${id}`).join('、')} 没有结论）`),
      advice: adviceForCode('timeout'),
      reasons: Object.freeze([...run.statustexts]),
    });
  }

  private onStalled(run: ActiveRun): void {
    if (run.concluded || this.run !== run) return;
    const window = run.info.stallTimeoutMs ?? 0;
    const missing = this.missingCompasses(run);
    this.conclude(run, 'failed', {
      code: 'stalled',
      text:
        `已经 ${Math.round(window / 1000)} 秒没有收到任何校准进度` +
        (missing.length === 0 ? '' : `（罗盘 ${missing.map((id) => `#${id}`).join('、')} 还没有结论）`) +
        '：飞控那边的校准可能已经停了',
      advice: adviceForCode('stalled'),
      reasons: Object.freeze([...run.statustexts]),
    });
  }

  /* ────────────────────── 内部：遥测 ────────────────────── */

  private handleFrame(frame: MavlinkFrame): void {
    if (this.disposed) return;

    if (frame.messageId === COMMAND_LONG_ID) {
      // 加速度计六面的"请摆成某个方向"就是这样进来的：飞控用一条命令**指挥**地面站提示用户
      this.handleInboundCommandLong(frame);
      return;
    }

    const run = this.run;
    if (run === null || run.concluded) return;
    if (frame.messageId !== MAG_PROGRESS_ID && frame.messageId !== MAG_REPORT_ID) return;

    const definition = messageById(frame.messageId);
    if (definition === undefined) return;
    let values: DecodedValues;
    try {
      values = decodePayload(definition, frame.payload);
    } catch {
      // 一帧解不开不能让整条遥测停摆（与 `vehicle.ts` 同一条理由）
      return;
    }

    if (frame.messageId === MAG_PROGRESS_ID) this.handleMagProgress(run, values);
    else this.handleMagReport(run, values);
  }

  private handleInboundCommandLong(frame: MavlinkFrame): void {
    const definition = messageById(frame.messageId);
    if (definition === undefined) return;
    let values: DecodedValues;
    try {
      values = decodePayload(definition, frame.payload);
    } catch {
      return;
    }
    const command = numberField(values, 'command');
    if (command === null || ACCELCAL_COMMAND_ID === null || Math.round(command) !== ACCELCAL_COMMAND_ID) {
      return;
    }

    // 飞控在等我们对这条命令的确认；不回它，它就一直等下去（真机上表现为"卡在第一个方向"）
    void this.sendAck(ACCELCAL_COMMAND_ID);

    const run = this.run;
    if (run === null || run.concluded || run.info.conclusion !== 'accel-positions') return;

    const position = Math.round(numberField(values, 'param1') ?? 0);
    this.touchStall(run);

    if (ACCEL_POSITION_SUCCESS !== null && position === ACCEL_POSITION_SUCCESS) {
      this.concludeSuccess(run, `${run.info.label}完成`, '飞控报告六个方向的取样都已完成。');
      return;
    }
    if (ACCEL_POSITION_FAILED !== null && position === ACCEL_POSITION_FAILED) {
      this.conclude(run, 'failed', {
        code: 'rejected',
        text: `${run.info.label}失败：飞控报告取样不通过（多数是某个方向摆得不够准，或机体被碰动了）`,
        advice: '卸掉桨后按提示重新摆一遍：每个方向都放稳、静止，再点"已摆好"。',
        reasons: Object.freeze([...run.statustexts]),
      });
      return;
    }
    if (position < 1 || position > ACCEL_POSITION_TOTAL) return;

    const positionName = enumEntryName('ACCELCAL_VEHICLE_POS', position);
    const text =
      (positionName === null ? undefined : ACCEL_POSITION_TEXT[positionName]) ??
      `把机体摆成飞控要求的第 ${position} 个方向`;
    const prompt: AccelPositionPrompt = Object.freeze({
      position,
      positionName,
      // 飞控可能重发同一个方向的提示：按"又换了一个方向"来数会跳号，所以只在方向变化时递增
      index:
        run.prompt === null || run.prompt.position !== position
          ? Math.min((run.prompt?.index ?? 0) + 1, ACCEL_POSITION_TOTAL)
          : run.prompt.index,
      total: ACCEL_POSITION_TOTAL,
      text,
      at: this.now(),
    });
    run.prompt = prompt;
    run.phase = 'awaiting-position';
    run.message = `第 ${prompt.index}/${ACCEL_POSITION_TOTAL} 步：${text}`;
    this.publish();
    this.events.emit('prompt', { prompt, kind: run.info.kind, at: prompt.at });
  }

  private async sendAck(command: number): Promise<void> {
    const accepted = enumValue('MAV_RESULT', 'MAV_RESULT_ACCEPTED');
    if (accepted === null) return;
    try {
      await this.vehicle.session.send('COMMAND_ACK', {
        command,
        result: accepted,
        target_system: this.vehicle.systemId,
        target_component: this.vehicle.componentId,
      });
    } catch {
      // 回不了确认不是本地错误：飞控会重发同样的提示，用户仍能完成这一步
    }
  }

  private handleMagProgress(run: ActiveRun, values: DecodedValues): void {
    const at = this.now();
    const status = Math.round(numberField(values, 'cal_status') ?? -1);
    const info = magCalStatusInfo(status);
    const compassId = Math.round(numberField(values, 'compass_id') ?? 0);
    const attempt = Math.round(numberField(values, 'attempt') ?? 0);
    const completionPct = clampPercent(numberField(values, 'completion_pct') ?? 0);
    const calMask = Math.round(numberField(values, 'cal_mask') ?? 0);
    const dx = numberField(values, 'direction_x') ?? 0;
    const dy = numberField(values, 'direction_y') ?? 0;
    const dz = numberField(values, 'direction_z') ?? 0;

    this.noteMask(run, calMask);
    this.touchStall(run);

    const progress: CompassProgress = Object.freeze({
      compassId,
      completionPct,
      attempt,
      calMask,
      completionMask: Object.freeze(numberArrayField(values, 'completion_mask')),
      status,
      statusName: magCalStatusName(status),
      running: info.running,
      direction: dx === 0 && dy === 0 && dz === 0 ? null : Object.freeze({ x: dx, y: dy, z: dz }),
      text: `罗盘 #${compassId}：${completionPct}%（第 ${attempt + 1} 次尝试），${info.reason}`,
      at,
    });
    run.progress.set(compassId, progress);
    run.phase = 'running';
    run.message = progress.text;
    this.publish();
    this.emitProgress(run, progress.text);
  }

  private handleMagReport(run: ActiveRun, values: DecodedValues): void {
    const at = this.now();
    const status = Math.round(numberField(values, 'cal_status') ?? -1);
    const info = magCalStatusInfo(status);
    const compassId = Math.round(numberField(values, 'compass_id') ?? 0);
    const calMask = Math.round(numberField(values, 'cal_mask') ?? 0);

    this.noteMask(run, calMask);
    this.touchStall(run);

    if (info.running) {
      // 飞控在校准**开始时**也会发一条 `MAG_CAL_REPORT`（`WAITING_TO_START` / `RUNNING_STEP_*`）。
      // 把它当结论，每一次校准都会在开工的瞬间"结束"。
      const existing = run.progress.get(compassId);
      const progress: CompassProgress = Object.freeze({
        compassId,
        completionPct: existing?.completionPct ?? 0,
        attempt: existing?.attempt ?? 0,
        calMask,
        completionMask: existing?.completionMask ?? Object.freeze([]),
        status,
        statusName: magCalStatusName(status),
        running: true,
        direction: existing?.direction ?? null,
        text: `罗盘 #${compassId}：${info.reason}`,
        at,
      });
      run.progress.set(compassId, progress);
      run.phase = 'running';
      run.message = progress.text;
      this.publish();
      this.emitProgress(run, progress.text);
      return;
    }

    const result = this.buildCompassResult(values, compassId, status, info, at);
    run.results.set(compassId, result);
    // 结论**替换**该罗盘的进度行（而不是删掉它）：多罗盘时用户可以逐个看到"哪一个已经好了"，
    // 删掉会让那一行在飞控报出结论的瞬间从界面上消失，看起来像"进度倒回去了"
    const previous = run.progress.get(compassId);
    run.progress.set(
      compassId,
      Object.freeze({
        compassId,
        completionPct: 100,
        attempt: previous?.attempt ?? 0,
        calMask,
        completionMask: previous?.completionMask ?? Object.freeze([]),
        status,
        statusName: magCalStatusName(status),
        running: false,
        direction: null,
        text: `罗盘 #${compassId}：${result.reason}`,
        at,
      }),
    );
    run.phase = 'running';
    run.message = `罗盘 #${compassId}：${result.reason}`;
    this.publish();
    this.emitProgress(run, run.message);

    if (this.allExpectedReported(run)) this.concludeMag(run);
  }

  private buildCompassResult(
    values: DecodedValues,
    compassId: number,
    status: number,
    info: MagCalStatusInfo,
    at: number,
  ): CompassResult {
    // fitness 为 0（或负数）时**不留**数值：在一句"校准失败"旁边跟着"fitness 0：拟合干净"
    // 比什么都不说更糟，而真实的 0 只会出现在"根本没跑起来"的报告里。
    const rawFitness = numberField(values, 'fitness');
    const fitness = rawFitness === null || rawFitness <= 0 ? null : rawFitness;
    const known = this.knownParam('COMPASS_CAL_FIT');
    const assessment =
      fitness === null
        ? null
        : assessFitness(
            fitness,
            known === null ? Number.NaN : known.value,
            known !== null && known.fromVehicle,
          );

    const oldName = enumEntryName(
      'MAV_SENSOR_ORIENTATION',
      Math.round(numberField(values, 'old_orientation') ?? -1),
    );
    const newName = enumEntryName(
      'MAV_SENSOR_ORIENTATION',
      Math.round(numberField(values, 'new_orientation') ?? -1),
    );
    const confidence = numberField(values, 'orientation_confidence');
    const orientation =
      oldName === null && newName === null
        ? null
        : Object.freeze({
            oldName,
            newName,
            confidence,
            fixed: newName !== null && newName !== oldName,
            text:
              newName !== null && newName !== oldName
                ? `飞控修正了罗盘安装方向：${oldName ?? '未知'} → ${newName}（置信度 ${confidence ?? 0}）`
                : `罗盘安装方向没有变化（${newName ?? oldName ?? '未知'}）`,
          });

    return Object.freeze({
      compassId,
      status,
      statusName: magCalStatusName(status),
      outcome: info.success ? 'success' : 'failed',
      reason: info.reason,
      advice: info.advice,
      fitness,
      fitnessAssessment: assessment,
      offsets: Object.freeze({
        x: numberField(values, 'ofs_x') ?? 0,
        y: numberField(values, 'ofs_y') ?? 0,
        z: numberField(values, 'ofs_z') ?? 0,
      }),
      diag: Object.freeze({
        x: numberField(values, 'diag_x') ?? 0,
        y: numberField(values, 'diag_y') ?? 0,
        z: numberField(values, 'diag_z') ?? 0,
      }),
      offdiag: Object.freeze({
        x: numberField(values, 'offdiag_x') ?? 0,
        y: numberField(values, 'offdiag_y') ?? 0,
        z: numberField(values, 'offdiag_z') ?? 0,
      }),
      orientation,
      autosaved: (numberField(values, 'autosaved') ?? 0) >= 0.5,
      at,
    });
  }

  private noteMask(run: ActiveRun, mask: number): void {
    for (const bit of maskBits(mask)) run.expected.add(bit);
  }

  private missingCompasses(run: ActiveRun): readonly number[] {
    const missing: number[] = [];
    for (const id of run.expected) {
      if (!run.results.has(id)) missing.push(id);
    }
    return missing.sort((a, b) => a - b);
  }

  /**
   * 所有期望的罗盘都有结论了吗。
   *
   * 期望集合来自三处：用户勾的罗盘、进度帧的 `cal_mask`、结论帧的 `cal_mask`。
   * 三处都没有时（飞控没说覆盖了几个罗盘），只能认为"报了的就算全部" ——
   * 否则只能等到绝对超时（3 分钟）才给结论，那比"可能少算一个罗盘"更糟。
   * 结论文案里会把有结论的罗盘逐个列出来，不让用户以为还有别的罗盘在跑。
   */
  private allExpectedReported(run: ActiveRun): boolean {
    if (run.expected.size === 0) {
      for (const id of run.results.keys()) run.expected.add(id);
      return run.results.size > 0;
    }
    for (const id of run.expected) {
      if (!run.results.has(id)) return false;
    }
    return true;
  }

  private handleStatustext(entry: StatustextEntry): void {
    if (this.disposed) return;
    const run = this.run;
    // 只留"本次校准期间"的状态文本：结论里的 `reasons` 要回答的是"飞控为**这一次**校准说了什么"，
    // 把上一条命令的解释混进来会让用户按错误的提示去排查。
    if (run === null || run.concluded) return;
    run.statustexts.push(entry);
    if (run.statustexts.length > STATUSTEXT_KEEP) run.statustexts.shift();
  }

  private handleParamValue(name: string, value: number): void {
    if (this.disposed || !Number.isFinite(value)) return;
    this.receivedParams.set(name, value);
    const waiter = this.readbackWaiters.get(name);
    if (waiter === undefined) return;
    this.readbackWaiters.delete(name);
    this.timers.clear(waiter.handle);
    waiter.settle(value);
  }

  private handleLinkState(change: LinkStateEvent): void {
    if (this.disposed) return;
    const run = this.run;
    if (run === null || run.concluded) return;
    if (!LINK_LOST_STATES.has(change.state)) return;
    // 校准要几十秒：断链时飞控那边可能已经写完了参数、也可能没有。这里如实说"结果未知"，
    // 而不是替飞控编一个"失败"（`CalibrationPhase.interrupted` 就是为这件事留的）。
    this.conclude(run, 'interrupted', {
      code: 'link-lost',
      text: `链路断开（${change.state}）：${run.info.label}的结果未知${
        change.detail === undefined ? '' : `（${change.detail}）`
      }`,
      advice: adviceForCode('link-lost'),
      reasons: Object.freeze([...run.statustexts]),
    });
  }

  /* ────────────────────── 内部：结论 ────────────────────── */

  private concludeMag(run: ActiveRun): void {
    const results = [...run.results.values()].sort((a, b) => a.compassId - b.compassId);
    const failed: CompassResult[] = [];
    for (const result of results) {
      if (result.outcome === 'failed') failed.push(result);
    }

    if (failed.length === 0) {
      // `autosaved` 如实反映飞控报的：多个罗盘时只要有一个没保存，整体就算"没保存"
      run.autosaved = results.length > 0 && results.every((result) => result.autosaved);
      run.acceptAvailable = !run.autosaved;
      this.acceptedMask = run.mask;
      const detail = results
        .map((result) => `罗盘 #${result.compassId}：${result.fitnessAssessment?.text ?? result.reason}`)
        .join('；');
      this.concludeSuccess(
        run,
        `${run.info.label}成功${detail.length === 0 ? '' : `（${detail}）`}`,
        run.autosaved
          ? '飞控已经自动保存了这次结果。'
          : '飞控没有自动保存：确认结果后点"接受校准"写入参数，否则重启后这次校准就丢了。',
      );
      return;
    }

    let first: CompassResult | null = null;
    for (const result of failed) {
      if (first === null) first = result;
    }
    // 失败原因取**第一个**失败罗盘的具体原因（方向不对 / 半径不对 / 残差过大…），
    // 而不是笼统的"校准失败"：这三者的下一步完全不同。
    const code = first === null ? 'mag-generic' : (magCalStatusInfo(first.status).failure ?? 'mag-generic');
    const text = failed.map((result) => `罗盘 #${result.compassId}校准失败：${result.reason}`).join('；');
    const okCount = results.length - failed.length;
    this.conclude(run, 'failed', {
      code,
      text: okCount > 0 ? `${text}（另有 ${okCount} 个罗盘已经成功）` : text,
      advice: first?.advice ?? null,
      reasons: Object.freeze([...run.statustexts]),
    });
  }

  private concludeSuccess(run: ActiveRun, text: string, advice: string | null): void {
    this.conclude(run, 'success', null, { text, advice });
  }

  private conclude(
    run: ActiveRun,
    outcome: CalibrationOutcome,
    failure: CalibrationFailure | null,
    summary?: { readonly text: string; readonly advice: string | null },
  ): void {
    if (run.concluded) return;
    run.concluded = true;
    this.clearRunTimers(run);

    const text = summary?.text ?? failure?.text ?? `${run.info.label}结束`;
    const advice = summary === undefined ? (failure?.advice ?? null) : summary.advice;

    // 成功之后才回读参数（失败时回读没有意义，还白占链路）
    const readbackNames = outcome === 'success' ? run.info.readbackParams : [];
    run.readback = this.synchronousReadback(readbackNames);
    this.lastReadback = run.readback;

    const report = this.makeReport(run.info, outcome, failure, {
      text,
      advice,
      compasses: [...run.results.values()].sort((a, b) => a.compassId - b.compassId),
      autosaved: run.autosaved,
      command: run.command,
      readback: run.readback,
      startedAt: run.startedAt,
    });

    run.phase = phaseForOutcome(outcome);
    run.message = text;
    this.run = null;
    this.lastReport = report;
    this.acceptAvailable = run.acceptAvailable;
    this.publish();
    this.events.emit('result', { report, at: report.finishedAt });
    run.settle(report);

    if (readbackNames.length > 0) void this.fillReadback(report);
  }

  private makeReport(
    info: CalibrationKindInfo | null,
    outcome: CalibrationOutcome,
    failure: CalibrationFailure | null,
    extra: {
      readonly text?: string;
      readonly advice?: string | null;
      readonly compasses?: readonly CompassResult[];
      readonly autosaved?: boolean | null;
      readonly command?: CommandResult | null;
      readonly readback?: readonly ParamReadback[];
      readonly startedAt?: number;
      readonly unsupportedReason?: string | null;
    } = {},
  ): CalibrationReport {
    const at = this.now();
    const startedAt = extra.startedAt ?? at;
    return Object.freeze({
      outcome,
      kind: info?.kind ?? null,
      kindLabel: info?.label ?? null,
      text: extra.text ?? failure?.text ?? '',
      advice: extra.advice === undefined ? (failure?.advice ?? null) : extra.advice,
      failure,
      compasses: Object.freeze([...(extra.compasses ?? [])]),
      autosaved: extra.autosaved ?? null,
      command: extra.command ?? null,
      readback: extra.readback ?? Object.freeze([]),
      unsupportedReason: extra.unsupportedReason ?? info?.unsupportedReason ?? null,
      startedAt,
      finishedAt: at,
      durationMs: at - startedAt,
    });
  }

  /** 不支持/被拒绝这种"没有跑起来"的结论：事件照发，只是没有遥测来源。 */
  private standaloneReport(
    info: CalibrationKindInfo | null,
    unsupportedReason: string | null,
    outcome: CalibrationOutcome,
    failure: CalibrationFailure,
  ): CalibrationReport {
    const report = this.makeReport(info, outcome, failure, {
      text: failure.text,
      advice: failure.advice,
      unsupportedReason,
    });
    this.lastReport = report;
    this.acceptAvailable = false;
    this.publish();
    this.events.emit('result', { report, at: report.finishedAt });
    return report;
  }

  /* ────────────────────── 内部：参数回读 ────────────────────── */

  /**
   * 取一个已知参数的当前值。
   *
   * 先看本次会话收到的 `PARAM_VALUE`，再看参数管理器的缓存（两者都是"飞控自己报过的值"）。
   * 拿不到时返回 `null`，而不是拿 ArduPilot 的默认值冒充 —— 判据（`COMPASS_CAL_FIT`）
   * 用错会把"其实合格"判成"偏高"，所以文案里会写清用的是哪个阈值。
   */
  private knownParam(name: string): { readonly value: number; readonly fromVehicle: boolean } | null {
    const received = this.receivedParams.get(name);
    if (received !== undefined) return { value: received, fromVehicle: true };
    const cached = this.params?.get(name);
    if (cached !== undefined && Number.isFinite(cached.value)) {
      return { value: cached.value, fromVehicle: true };
    }
    return null;
  }

  private synchronousReadback(names: readonly string[]): readonly ParamReadback[] {
    const out: ParamReadback[] = [];
    for (const name of names) {
      const known = this.knownParam(name);
      out.push(
        known === null
          ? this.readbackEntry(name, null, null)
          : this.readbackEntry(name, known.value, 'cache'),
      );
    }
    return Object.freeze(out);
  }

  private readbackEntry(
    name: string,
    value: number | null,
    source: 'vehicle' | 'cache' | null,
  ): ParamReadback {
    const meta = this.paramMetadata?.get(name);
    const label = meta?.label ?? null;
    const units = meta?.units ?? null;
    const text =
      value === null
        ? `${name}：没有拿到值（飞控没有回应这条回读请求）`
        : `${name} = ${value}${units === null || units.length === 0 ? '' : ` ${units}`}${
            label === null ? '' : `（${label}）`
          }`;
    return Object.freeze({ name, value, source, label, units, text, at: this.now() });
  }

  /** 缓存里没有的参数，逐个点名问飞控（`PARAM_REQUEST_READ`），拿到就更新界面。 */
  private async fillReadback(report: CalibrationReport): Promise<void> {
    const missing = report.readback.filter((entry) => entry.value === null);
    if (missing.length === 0) return;
    const filled = new Map<string, ParamReadback>();
    for (const entry of missing) {
      if (this.disposed) return;
      const value = await this.requestParam(entry.name);
      filled.set(entry.name, this.readbackEntry(entry.name, value, value === null ? null : 'vehicle'));
    }
    if (this.disposed) return;
    this.lastReadback = Object.freeze(report.readback.map((entry) => filled.get(entry.name) ?? entry));
    this.publish();
  }

  private async requestParam(name: string): Promise<number | null> {
    const session = this.vehicle.session;
    const linkState = session.state;
    // 'silent' 是"链路开了但还没收到遥测"，那时请求是合法的（正是在请对端说点什么）
    if (linkState === 'idle' || linkState === 'connecting' || linkState === 'error') return null;

    const settled = new Promise<number | null>((resolve) => {
      const handle = this.timers.set(() => {
        this.readbackWaiters.delete(name);
        resolve(null);
      }, this.readbackTimeoutMs);
      this.readbackWaiters.set(name, { handle, settle: resolve });
    });

    try {
      await session.send('PARAM_REQUEST_READ', {
        target_system: this.vehicle.systemId,
        target_component: this.vehicle.componentId,
        param_id: name,
        // -1 = "按名字找"：对端参数表顺序变了也不会读错参数
        param_index: -1,
      });
    } catch {
      const waiter = this.readbackWaiters.get(name);
      if (waiter !== undefined) {
        this.readbackWaiters.delete(name);
        this.timers.clear(waiter.handle);
        waiter.settle(null);
      }
    }
    return settled;
  }

  private clearReadbackWaiters(): void {
    for (const waiter of this.readbackWaiters.values()) {
      this.timers.clear(waiter.handle);
      waiter.settle(null);
    }
    this.readbackWaiters.clear();
  }

  /* ────────────────────── 内部：快照与事件 ────────────────────── */

  private publish(): void {
    this.snapshot = this.buildState();
    this.events.emit('state', { state: this.snapshot, at: this.now() });
  }

  private buildState(): CalibrationState {
    const run = this.run;
    if (run === null) {
      const report = this.lastReport;
      return Object.freeze({
        phase: report === null ? 'idle' : phaseForOutcome(report.outcome),
        kind: report?.kind ?? null,
        kindLabel: report?.kindLabel ?? null,
        startedAt: report?.startedAt ?? null,
        elapsedMs: report?.durationMs ?? 0,
        progressPct: null,
        compasses: Object.freeze([]),
        attempt: null,
        prompt: null,
        message: report?.text ?? '还没有开始校准',
        canAccept: this.acceptAvailable,
        readback: this.lastReadback,
        report,
      });
    }

    const compasses = [...run.progress.values()].sort((a, b) => a.compassId - b.compassId);
    const overall = this.overallProgress(compasses);
    return Object.freeze({
      phase: run.phase,
      kind: run.info.kind,
      kindLabel: run.info.label,
      startedAt: run.startedAt,
      elapsedMs: this.now() - run.startedAt,
      progressPct: overall.progressPct,
      compasses: Object.freeze(compasses),
      attempt: overall.attempt,
      prompt: run.prompt,
      message: run.message,
      canAccept: false,
      readback: this.lastReadback,
      report: this.lastReport,
    });
  }

  /**
   * 整体进度与尝试次数。
   *
   * 整体进度取**最慢**的那个罗盘：取平均会让"3 个好了、2 个没好"显示成 60%，
   * 用户以为快完了，其实还差两个。
   */
  private overallProgress(compasses: readonly CompassProgress[]): {
    readonly progressPct: number | null;
    readonly attempt: number | null;
  } {
    let progressPct: number | null = null;
    let attempt: number | null = null;
    for (const compass of compasses) {
      progressPct =
        progressPct === null ? compass.completionPct : Math.min(progressPct, compass.completionPct);
      attempt = attempt === null ? compass.attempt : Math.max(attempt, compass.attempt);
    }
    return { progressPct, attempt };
  }

  private emitProgress(run: ActiveRun, text: string): void {
    const compasses = [...run.progress.values()].sort((a, b) => a.compassId - b.compassId);
    const overall = this.overallProgress(compasses);
    this.events.emit('progress', {
      kind: run.info.kind,
      kindLabel: run.info.label,
      phase: run.phase,
      progressPct: overall.progressPct,
      attempt: overall.attempt,
      compasses: Object.freeze(compasses),
      text,
      at: this.now(),
    });
  }
}
